import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  type CardDispute,
  type CardDisputeDocumentEvidenceInput,
  CardDisputeNextAction,
  cardsGetCardTransactionOptions,
} from '@column/api';
import { Button } from '@column/column-ui-kit';
import { Alert, AlertDescription, AlertTitle, Label, Textarea } from '@column/ui';

import { DisputeEvidenceComposer } from '../Evidence/DisputeEvidenceComposer';
import {
  apiErrorDetail,
  apiErrorMessage,
  useAddCardDisputeEvidence,
  useCanManageCardDisputes,
  useSubmitCardDispute,
} from '~/lib/api/cardDisputes';
import { getDisputeReasonLabel } from '~/lib/disputes/labels';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { DisputeReasonForm } from './DisputeReasonForm';
import {
  type DisputeReasonFormState,
  NARRATIVE_TOO_LONG_MESSAGE,
  REASON_REQUIRES_NARRATIVE,
  REASON_WITHOUT_DETAILS,
  buildDraftRepairCalls,
  disputeAmountHardStop,
  disputeReasonOf,
  disputeRequirementContext,
  disputeSubmissionReadiness,
  evidenceGuidance,
  findReasonHardStops,
  missingReasonFields,
  narrativeTooLong,
  reasonForPartialDisputeTooLong,
  reasonFormStateFromDetails,
  reasonRequiresPartialExplanation,
} from './questionnaire';
import { ReasonEvidenceGuidance, ReasonField } from './ReasonFields';

export interface DisputeDraftRepairPanelProps {
  dispute: CardDispute;
  onUpdated: () => void;
}

/**
 * Draft workspace on the dispute detail page: corrects questionnaire answers and the partial
 * explanation, adds narrative and document evidence, and submits the finished draft. Amount,
 * currency, transaction, and reason are immutable after creation, so the panel never edits them;
 * a wrong claim is closed with Part 05 and recreated.
 */
export const DisputeDraftRepairPanel: React.FC<DisputeDraftRepairPanelProps> = ({ dispute, onUpdated }) => {
  const canManage = useCanManageCardDisputes();
  const reason = disputeReasonOf(dispute);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const contextKey = `${dispute.id}:${platformId ?? ''}:${isSandbox ? 'sandbox' : 'live'}`;
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;
  const previousContextKeyRef = useRef(contextKey);

  const [formState, setFormState] = useState<DisputeReasonFormState>(() =>
    reasonFormStateFromDetails(reason ?? 'other', dispute.reason_details, dispute.currency)
  );
  const [partialExplanation, setPartialExplanation] = useState(dispute.reason_for_partial_dispute ?? '');
  const [narrative, setNarrative] = useState('');
  const [evidence, setEvidence] = useState<CardDisputeDocumentEvidenceInput[]>([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const [showValidation, setShowValidation] = useState(false);

  const addEvidenceMutation = useAddCardDisputeEvidence({
    disputeId: dispute.id,
    transactionId: dispute.card_transaction_id,
  });
  const submitMutation = useSubmitCardDispute();

  // Conditional requirements read the disputed transaction (MCC, processing date, amount). Until it
  // loads, or if it cannot, those questions stay optional rather than blocking the draft.
  const transactionQuery = useQuery({
    ...cardsGetCardTransactionOptions({ path: { card_transaction_id: dispute.card_transaction_id } }),
    enabled: canManage && Boolean(reason) && dispute.submission_status === 'draft',
    retry: false,
  });
  const requirementContext = useMemo(() => disputeRequirementContext(transactionQuery.data), [transactionQuery.data]);

  useEffect(() => {
    if (previousContextKeyRef.current === contextKey) {
      return;
    }
    previousContextKeyRef.current = contextKey;
    setFormState(reasonFormStateFromDetails(reason ?? 'other', dispute.reason_details, dispute.currency));
    setPartialExplanation(dispute.reason_for_partial_dispute ?? '');
    setNarrative('');
    setEvidence([]);
    setSaveError(undefined);
    setShowValidation(false);
  }, [contextKey, dispute, reason]);

  const attachedNarrative = dispute.evidence.some((entry) => entry.narrative && entry.narrative.trim() !== '');
  const attachedDocumentCount = dispute.evidence.filter((entry) => entry.document_id).length;
  const missingFields = reason ? missingReasonFields(reason, formState, dispute.currency, requirementContext) : [];
  const hardStops = reason ? findReasonHardStops(reason, formState, dispute.currency, requirementContext) : [];
  // The stored claim amount against the transaction's disputable amount. The backend re-checks it on every details merge
  // and at submission, but next_actions does not, so it is surfaced here.
  const amountHardStop = reason
    ? disputeAmountHardStop({
        reason,
        claimAmount: dispute.amount,
        transactionAmount: requirementContext.transaction?.disputableAmount,
        formState,
        currency: dispute.currency,
      })
    : undefined;
  const narrativeOverLimit = narrativeTooLong(narrative);

  // The partial-claim requirement is server-owned knowledge (the DTO does not expose it), so the
  // field is offered only on authoritative signals: an explanation already stored on the draft, or
  // a submit attempt the server rejected naming this field. An unexplained server gap is NOT a
  // partial claim — treating it as one demanded a fabricated explanation for full-amount drafts.
  const [partialRequiredByServer, setPartialRequiredByServer] = useState(false);
  const partialExplanationVisible = Boolean(dispute.reason_for_partial_dispute) || partialRequiredByServer;
  // next_actions evaluates the questionnaire with the disputed transaction loaded (MCC, processing date), the same
  // context this panel reads, so the two agree on transaction-dependent answers. Where only one side reports a gap,
  // submitting is allowed so the server can name the field; the amount hard stops are the one gap it never reports.
  const serverReady = dispute.next_actions.includes(CardDisputeNextAction.SUBMIT) && !amountHardStop;

  const readiness = useMemo(
    () =>
      reason
        ? disputeSubmissionReadiness({
            reason,
            formState,
            currency: dispute.currency,
            context: requirementContext,
            narrative,
            hasAttachedNarrativeEvidence: attachedNarrative,
            evidence,
            attachedDocumentCount,
            requiresPartialExplanation: partialExplanationVisible && reasonRequiresPartialExplanation(reason),
            partialExplanation,
          })
        : { ready: false, missing: ['a supported dispute reason'] },
    [
      reason,
      formState,
      dispute.currency,
      requirementContext,
      narrative,
      attachedNarrative,
      evidence,
      attachedDocumentCount,
      partialExplanationVisible,
      partialExplanation,
    ]
  );

  if (!canManage || !reason || dispute.submission_status !== 'draft') {
    return null;
  }

  const repairCalls = buildDraftRepairCalls({
    reason,
    currency: dispute.currency,
    context: requirementContext,
    storedDetails: dispute.reason_details,
    formState,
    storedPartialExplanation: dispute.reason_for_partial_dispute ?? undefined,
    partialExplanationVisible,
    nextPartialExplanation: partialExplanation,
    narrative,
    evidence,
  });
  // A hard-stopped answer or amount only blocks a save that would send the questionnaire, which the backend validates
  // merged with the stored answers and amount; narrative and documents can still be added to such a draft.
  const detailsBlocked =
    (hardStops.length > 0 || Boolean(amountHardStop)) && repairCalls.some((call) => call.reason_details !== undefined);
  const canSave =
    !saving &&
    !uploadBusy &&
    repairCalls.length > 0 &&
    !detailsBlocked &&
    !narrativeOverLimit &&
    !reasonForPartialDisputeTooLong(partialExplanation);

  const runSave = async () => {
    if (repairCalls.length === 0) {
      return;
    }

    const failures: string[] = [];
    setSaving(true);
    setSaveError(undefined);

    // Each call is persisted on its own; details/partial are idempotent replaces, while narrative
    // and documents are appends — those are cleared locally as soon as they succeed so a retry
    // after a later failure never re-sends them and duplicate the evidence.
    for (const body of repairCalls) {
      if (contextKeyRef.current !== contextKey) {
        setSaving(false);
        return;
      }
      try {
        await addEvidenceMutation.mutateAsync({ evidence: body });
        if (body.narrative !== undefined) {
          setNarrative('');
        }
        if (body.document_id !== undefined) {
          setEvidence((current) => current.filter((entry) => entry.document_id !== body.document_id));
        }
      } catch (error) {
        failures.push(error instanceof Error && error.message ? error.message : 'A change could not be saved.');
        // Stop at the first failure: later calls stay unsaved and retryable, earlier ones stay persisted.
        break;
      }
    }

    if (contextKeyRef.current !== contextKey) {
      return;
    }
    setSaving(false);
    if (failures.length > 0) {
      setSaveError(failures[0]);
      addDangerNotification({ content: failures[0] });
      return;
    }
    addSuccessNotification({ content: 'Draft updated' });
    onUpdated();
  };

  const handleSubmit = async () => {
    if (amountHardStop) {
      return;
    }
    if (!serverReady && !readiness.ready) {
      setShowValidation(true);
      return;
    }
    if (repairCalls.length > 0) {
      return;
    }
    setSaveError(undefined);
    try {
      await submitMutation.mutateAsync({ disputeId: dispute.id, transactionId: dispute.card_transaction_id });
      addSuccessNotification({ content: 'Submission queued. The dispute is on its way to the Card Network.' });
      onUpdated();
    } catch (error) {
      const message = apiErrorMessage(error, 'The dispute could not be submitted.');
      // The submit error names the blocking field in its details; a partial-dispute requirement
      // surfaces here and reveals the explanation field.
      if (apiErrorDetail(error, 'field') === 'reason_for_partial_dispute') {
        setPartialRequiredByServer(true);
      }
      setSaveError(message);
      addDangerNotification({ content: message });
      onUpdated();
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4" data-testid="dispute-draft-repair">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">Draft dispute · {getDisputeReasonLabel(dispute.reason)}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Amount, currency, transaction, and reason are permanent. Answers and evidence below can be corrected until
            submission.
          </p>
        </div>
        <Button
          size="small"
          onClick={() => void handleSubmit()}
          // Unsaved repairs must reach the draft before submission — submitting with pending
          // changes would send the stale persisted values (or fail on an incomplete draft).
          isDisabled={Boolean(amountHardStop) || (!serverReady && !readiness.ready) || repairCalls.length > 0}
          isLoading={submitMutation.isPending}
        >
          Submit dispute
        </Button>
      </div>

      {amountHardStop ? (
        <Alert variant="destructive" data-testid="dispute-draft-amount-hard-stop">
          <AlertTitle>The Card Network will reject this claim amount</AlertTitle>
          <AlertDescription>
            {amountHardStop} The amount is permanent: correct the receipt amount below if it is wrong, or close this
            draft and create a new dispute for the right amount.
          </AlertDescription>
        </Alert>
      ) : serverReady ? (
        <Alert data-testid="dispute-draft-ready">
          <AlertTitle>Ready to submit</AlertTitle>
          <AlertDescription>
            This draft has everything the Card Network requires. Submitting queues the dispute for the network; it does
            not move money.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert data-testid="dispute-draft-incomplete">
          <AlertTitle>Submission requirements are not met yet</AlertTitle>
          <AlertDescription>
            {repairCalls.length > 0
              ? 'Save your changes below before submitting.'
              : readiness.missing.length > 0
                ? `Still needed: ${readiness.missing.join(', ')}.`
                : 'The server reports an unresolved requirement. Try submitting to see the specific field, then refresh.'}
          </AlertDescription>
        </Alert>
      )}

      {showValidation && missingFields.length > 0 && (
        <p className="text-xs text-destructive">Some required questionnaire answers are missing below.</p>
      )}

      {!REASON_WITHOUT_DETAILS.includes(reason) && (
        <DisputeReasonForm
          reason={reason}
          value={formState}
          onChange={setFormState}
          currency={dispute.currency}
          context={requirementContext}
          missingFields={showValidation ? missingFields : []}
        />
      )}

      {partialExplanationVisible && (
        <ReasonField
          label="Why is the claim less than the full transaction amount?"
          required
          description="Corrections replace the stored explanation. Up to 10,000 characters."
        >
          <Textarea
            value={partialExplanation}
            data-dd-privacy="hidden"
            data-testid="dispute-draft-partial-explanation"
            onChange={(event) => setPartialExplanation(event.target.value)}
          />
          {reasonForPartialDisputeTooLong(partialExplanation) && (
            <p className="text-xs text-destructive">The explanation is limited to 10,000 characters.</p>
          )}
        </ReasonField>
      )}

      <div>
        <Label className="text-sm font-medium text-foreground" htmlFor="dispute-draft-narrative">
          Written statement
        </Label>
        <p className="mt-1 text-xs text-muted-foreground">
          {attachedNarrative
            ? 'A statement is attached. Adding another keeps both.'
            : REASON_REQUIRES_NARRATIVE[reason]
              ? 'This reason requires a statement before submission.'
              : 'Optional for this reason.'}{' '}
          Up to 5,000 characters.
        </p>
        <Textarea
          id="dispute-draft-narrative"
          className="mt-2"
          value={narrative}
          disabled={uploadBusy || saving}
          data-dd-privacy="hidden"
          data-testid="dispute-draft-narrative"
          placeholder="Explain what happened"
          onChange={(event) => setNarrative(event.target.value)}
        />
        {narrativeOverLimit && (
          <p className="mt-1 text-xs text-destructive" data-testid="dispute-draft-narrative-error">
            {NARRATIVE_TOO_LONG_MESSAGE}
          </p>
        )}
      </div>

      <ReasonEvidenceGuidance guidance={evidenceGuidance(reason, formState, dispute.currency, requirementContext)} />

      <DisputeEvidenceComposer value={evidence} onChange={setEvidence} disabled={saving} onBusyChange={setUploadBusy} />

      {saveError && (
        <Alert variant="destructive">
          <AlertTitle>Changes were not fully saved</AlertTitle>
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="small"
          onClick={() => void runSave()}
          isDisabled={!canSave}
          isLoading={saving}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
};
